import {
  ButtonFlavor,
  ButtonSize,
  ButtonType,
  ExButton,
  ExCard,
  ExIcon,
  ExTab,
  ExTabItem,
  ExDialog,
  DialogVariant,
  DialogHeaderContent,
  ExLink,
  LinkSize,
  TargetVariant,
} from '@boomi/exosphere';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import './AgentBuilderDialog.scss';
import { useBoomiAgentFoundationContext } from '@context/BoomiAgentFoundationContext';
import UserPrivilege from '@utils/data/userPrivileges';
import { hasPrivilege } from '@utils/data/rbacService';
import Features from '@utils/features';
import AIAssistedTemplate from '@components/AgentBuilder/AIAssistedTemplate/AIAssistedTemplate';
import ImportAgentDialog from '@components/AgentBuilder/ImportAgentDialog/ImportAgentDialog';
import SourcesBuilderTabView from '@components/AgentBuilder/SourcesBuilderTabView/SourcesBuilderTabView';
import ModelBuilderTabView from '@components/AgentBuilder/ModelBuilderTabView/ModelBuilderTabView';

// `initialGoal` — set only when Agentstudio was opened from a Boomi Concierge
// deeplink carrying an agent-authored Goal Description. It skips the template
// menu and opens straight into Build with AI with the goal already filled, so
// the handoff lands the user one click from generating their agent.
function AgentBuilderDialog({ onCancel, initialGoal }: any) {
  const {
    builderDialogTitle,
    setBuilderDialogTitle,
    showPreview,
    setShowPreview,
    setShowBuilderDialog,
    resetWizardData,
    resetToolWizardData,
    // setWizardData,
    isReffererTool,
    setIsReffererTool,
    userPrivileges,
    userResponse,
    setCreateAgentMethod,
    abortRequests,
  } = useBoomiAgentFoundationContext();
  const [showDialogIcon, setShowDialogIcon] = useState(false);
  const [dialogIcon, setDialogIcon] = useState('');
  const [openImportAgentDialog, setOpenImportAgentDialog] = useState(false);
  // A handed-off goal means the user already chose "build with AI" back in the
  // Concierge — showing them the four-card menu first would make them say it
  // twice.
  const [openAIAssistedTemplate, setOpenAIAssistedTemplate] = useState(Boolean(initialGoal));
  const [isTool, setIsTool] = useState(false);
  const [isSourcesTabSelected, setIsSourcesTabSelected] = useState(false);
  const [isModelsTabSelected, setIsModelsTabSelected] = useState(false);

  const navigate = useNavigate();
  const [t] = useTranslation('common');
  const location = useLocation();
  useEffect(() => {
    if (location?.pathname.includes('/tools') || location?.pathname.includes('/add-tools')) setIsTool(true);
    if (location?.pathname.includes('/sources') || location?.pathname.includes('/add-source')) setIsSourcesTabSelected(true);
    // getTemplates().then((res: any) => {
    //   setTemplates(res.data?.items);
    //   setIsLoading(false);
    // }).catch(() => {
    //   setIsLoading(false);
    //   ToastController.show({
    //     description: 'Failed to load templates.',
    //     type: AlertToastType.ERROR,
    //   });
    // });
  }, []);

  const tools = ['Prompt', 'Integration', 'DataHub Query', 'API'];

  const getIconByType = (type: string): string => {
    switch (type) {
      case 'DataHub Query':
        return 'Hub';
      case 'Integration':
        return 'Integration';
      case 'Prompt':
        return 'Players';
      case 'API':
        return 'API';
      default:
        return 'DefaultIcon';
    }
  };

  const getDescriptionByType = (type: string): string => {
    switch (type) {
      case 'DataHub Query':
        return t('hub_tool_description');
      case 'Integration':
        return t('integration_tool_description');
      case 'Prompt':
        return t('prompt_tool_description');
      case 'API':
        return t('openapi_tool_description');
      default:
        return t('hub_tool_description');
    }
  };

  const handleClick = (type: any) => {
    setShowBuilderDialog(false);
    document.body.style.overflow = 'unset';
    resetToolWizardData();
    let toolType = '';
    if (type.toLowerCase() === 'api') {
      toolType = 'api';
    } else if (type.toLowerCase() === 'datahub query') {
      toolType = 'datahub';
    } else {
      toolType = type.toLowerCase();
    }
    navigate(`/add-tools?tool=${toolType}`);
  };
  const goBack = () => {
    setShowDialogIcon(false);
    setShowPreview(false);
    setOpenAIAssistedTemplate(false);
    setOpenImportAgentDialog(false);
    setBuilderDialogTitle(t('agent_builder_dialog_title_agent_designer'));
    document.body.style.overflow = 'unset';
  };
  // const openAgentBuilderWizard = (template: any) => {
  //   const updatedWizardData = transformApiResponseToWizardData(template);
  //   setWizardData(updatedWizardData);
  //   setShowBuilderDialog(false);
  //   document.body.style.overflow = 'unset';
  //   if (!template) {
  //     resetWizardData();
  //   }
  //   navigate('/agent-designer');
  // };
  const hasAgentCreatePermission = hasPrivilege(userPrivileges, UserPrivilege.AGENT_CREATE);
  // const message = t('ai_loading');
  return (
    <ExDialog
      open
      className={
            builderDialogTitle === t('agent_builder_dialog_title_agent_designer')
              ? 'agent-builder-dialog'
              : 'agent-goal'
      }
      dialogTitle={builderDialogTitle}
      onCancel={() => {
        setShowDialogIcon(false);
        abortRequests(); // Abort any ongoing API calls
        onCancel();
      }}
      hideLeadingIcon={dialogIcon !== 'error' && dialogIcon !== 'Check'}
      variant={DialogVariant.SPACED}
      {...(dialogIcon === 'error' && { headerContent: DialogHeaderContent.NEGATIVE })}
      {...(dialogIcon === 'Check' && { headerContent: DialogHeaderContent.POSITIVE })}
    >
      {/* {isLoading && (
        <div className="templates-loader-container">
          <ExLoader
            size={LoaderSize.MEDIUM}
            label={message}
            variant={LoaderVariant.SPINNER}
          />
        </div>
      )} */}
      {openAIAssistedTemplate && (
        <AIAssistedTemplate
          goBack={goBack}
          setShowBuilderDialog={setShowBuilderDialog}
          initialGoal={initialGoal}
        />
      )}
      {openImportAgentDialog && (
        <ImportAgentDialog
          goBack={goBack}
          setBuilderDialogTitle={setBuilderDialogTitle}
          setShowDialogIcon={setShowDialogIcon}
          onCancel={onCancel}
          showDialogIcon={showDialogIcon}
          setDialogIcon={setDialogIcon}
        />
      )}
      {/* {!isLoading && showPreview && !isTool && !isReffererTool && (
        <TemplatePreview
          goBack={goBack}
          selectedTemplate={selectedTemplate}
          selectedPreviewTemplate={selectedPreviewTemplate}
        />
      )} */}
      <div>
        {!showPreview && !openAIAssistedTemplate && !openImportAgentDialog && (
          <div className="tool-type-wrapper">
            <ExTab role="tablist">
              <ExTabItem
                selected={!isTool && !isReffererTool
                  && !isSourcesTabSelected && !isModelsTabSelected}
                data-testid="agent-designer-agent-tab"
                onClick={() => {
                  setIsTool(false);
                  setIsReffererTool(false);
                  setIsSourcesTabSelected(false);
                  setIsModelsTabSelected(false);
                }}
              >
                <div className="tab-title">{t('agents')}</div>
              </ExTabItem>
              <ExTabItem
                selected={isTool || isReffererTool}
                data-testid="agent-designer-tools-tab"
                onClick={() => {
                  setIsTool(true);
                  setIsSourcesTabSelected(false);
                  setIsModelsTabSelected(false);
                }}
              >
                <div className="tab-title">{t('tools')}</div>
              </ExTabItem>
              {Features.isSourcesManagementEnabled() && (
                <ExTabItem
                  selected={isSourcesTabSelected}
                  data-testid="agent-designer-sources-tab"
                  onClick={() => {
                    setIsTool(false);
                    setIsReffererTool(false);
                    setIsSourcesTabSelected(true);
                    setIsModelsTabSelected(false);
                  }}
                >
                  <div className="tab-title">{t('sources_tab_title')}</div>
                </ExTabItem>
              )}
              {Features.isAgentGardenByomEnabled(userResponse?.user) && (
                <ExTabItem
                  selected={isModelsTabSelected}
                  data-testid="agent-designer-models-tab"
                  onClick={() => {
                    setIsTool(false);
                    setIsReffererTool(false);
                    setIsSourcesTabSelected(false);
                    setIsModelsTabSelected(true);
                  }}
                >
                  <div className="tab-title">{t('models_tab_title')}</div>
                </ExTabItem>
              )}
            </ExTab>
          </div>
        )}
        {!showPreview
          && !openAIAssistedTemplate
          && !openImportAgentDialog
          && !isTool
          && !isReffererTool && !isSourcesTabSelected && !isModelsTabSelected && (
            <div className="designer-dialog">
              <div className="agent-tool-info">
                {t('agent_builder_text')}
                <ExLink
                  href="https://help.boomi.com/docs/Atomsphere/Platform/Building_and_deploying_agents"
                  target={TargetVariant.BLANK}
                  size={LinkSize.SMALL}
                  className="tooltip-link designer-dialog-link"
                  trailingIcon="Open in new window"
                >
                  {t('learn_more')}
                </ExLink>
              </div>
              <div className="template-cards-container">
                <ExCard
                  className="template-card"
                  data-testid="designer-dialog-agent-no-template-card"
                >
                  <div
                    slot="header"
                    className="template-header"
                    data-testid="template-header"
                  >
                    <div className="template-image-container">
                      <ExIcon
                        slot="icon"
                        className="template-agent-icon"
                        icon="Add new"
                        hideBrowserTooltip
                      />
                    </div>
                  </div>
                  <div className="template-content">
                    <div className="agent-name">
                      {t('ai_agent_garden_no_template')}
                    </div>
                    <div className="agent-description">
                      {t('ai_agent_build_agent_from_scratch')}
                    </div>
                  </div>
                  <div className="card-overlay">
                    <ExButton
                      flavor={ButtonFlavor.BRANDED}
                      type={ButtonType.PRIMARY}
                      size={ButtonSize.DEFAULT}
                      data-testid="manual-agent-designer-dialog-button"
                      onClick={() => {
                        setCreateAgentMethod('Added-manually');
                        goBack();
                        setShowBuilderDialog(false);
                        resetWizardData();
                        const { hash } = window.location;
                        if (hash.includes('/agent-designer')) {
                          const removeQuery = (url: string) => url.split('?')[0];
                          const cleanHash = removeQuery(hash);
                          const newUrl = `${window.location.origin}${window.location.pathname}${cleanHash}`;
                          window.history.replaceState(
                            {},
                            document.title,
                            newUrl,
                          );
                          window.location.reload();
                        } else if (
                          window.location.pathname.includes('/agent-designer')
                        ) {
                          const newUrl = window.location.origin + window.location.pathname;
                          window.history.replaceState(
                            {},
                            document.title,
                            newUrl,
                          );
                          window.location.reload();
                        } else {
                          navigate('/agent-designer');
                        }
                      }}
                    >
                      {t('agent_builder_dialog_manual')}
                    </ExButton>
                  </div>
                </ExCard>
                <ExCard
                  className="template-card"
                  data-testid="designer-dialog-agent-no-template-card"
                >
                  <div
                    slot="header"
                    className="template-header"
                    data-testid="template-header"
                  >
                    <div className="build-with-ai-image-container">
                      <ExIcon
                        slot="icon"
                        className="template-agent-icon"
                        icon="Auto arrange"
                        hideBrowserTooltip
                      />
                    </div>
                  </div>
                  <div className="template-content">
                    <div className="agent-name">
                      {t('agent_builder_dialog_ai')}
                    </div>
                    <div className="agent-description">
                      {t('agent_builder_dialog_ai_description')}
                    </div>
                  </div>
                  <div className="card-overlay">
                    <ExButton
                      flavor={ButtonFlavor.BRANDED}
                      type={ButtonType.PRIMARY}
                      size={ButtonSize.DEFAULT}
                      data-testid="ai-assisted-agent-designer-dialog-button"
                      onClick={() => {
                        resetWizardData();
                        setBuilderDialogTitle(t('ai_assisted_dialog_title'));
                        setOpenAIAssistedTemplate(true);
                      }}
                    >
                      {t('agent_builder_dialog_ai')}
                    </ExButton>
                  </div>
                </ExCard>
                {hasAgentCreatePermission && (
                  <ExCard
                    className="template-card"
                    data-testid="designer-dialog-agent-no-template-card"
                  >
                    <div
                      slot="header"
                      className="template-header"
                      data-testid="template-header"
                    >
                      <div className="import-agent-image-container">
                        <ExIcon
                          slot="icon"
                          icon="File upload"
                          hideBrowserTooltip
                          className="template-agent-icon"
                        />
                      </div>
                    </div>
                    <div className="template-content">
                      <div className="agent-name">
                        {t('ai_agent_garden_import_agent')}
                      </div>
                      <div className="agent-description">
                        {t('ai_agent_import_agent_description')}
                      </div>
                    </div>
                    <div className="card-overlay">
                      <ExButton
                        flavor={ButtonFlavor.BRANDED}
                        type={ButtonType.PRIMARY}
                        size={ButtonSize.DEFAULT}
                        data-testid="ai-assisted-agent-designer-import-file-button"
                        onClick={() => {
                          resetWizardData();
                          setBuilderDialogTitle(t('import_agent_dialog_title'));
                          setOpenImportAgentDialog(true);
                        }}
                      >
                        {t('agent_builder_dialog_import')}
                      </ExButton>
                    </div>
                  </ExCard>
                )}
                <ExCard
                  className="template-card"
                  data-testid="designer-dialog-agent-no-template-card"
                >
                  <div
                    slot="header"
                    className="template-header"
                    data-testid="template-header"
                  >
                    <div className="template-gallery-image-container">
                      <ExIcon
                        slot="icon"
                        className="template-agent-icon"
                        icon="Cards"
                        hideBrowserTooltip
                      />
                    </div>
                  </div>
                  <div className="template-content">
                    <div className="agent-name">
                      {t('agent_foundation_explore_gallery')}
                    </div>
                    <div className="agent-description">
                      {t('agent_foundation_explore_gallery_description')}
                    </div>
                  </div>
                  <div className="card-overlay">
                    <ExButton
                      flavor={ButtonFlavor.BRANDED}
                      type={ButtonType.PRIMARY}
                      size={ButtonSize.DEFAULT}
                      data-testid="explore-template-gallery-agent-designer-dialog-button"
                      onClick={() => {
                        setShowBuilderDialog(false);
                        document.body.style.overflow = 'unset';
                        navigate('/home', { state: { showGallery: true } });
                      }}
                    >
                      {t('agent_foundation_explore_gallery')}
                    </ExButton>
                  </div>
                </ExCard>
              </div>
            </div>
        )}
        {((!openAIAssistedTemplate && isTool)
          || (!openAIAssistedTemplate && isReffererTool)) && (
          <div className="designer-dialog">
            <div className="agent-tool-info">
              {t('tool_builder_text')}
              <ExLink
                href="https://help.boomi.com/docs/Atomsphere/Platform/adding_capabilities_to_agents"
                target={TargetVariant.BLANK}
                size={LinkSize.SMALL}
                className="tooltip-link designer-dialog-link"
                trailingIcon="Open in new window"
              >
                {t('learn_more')}
              </ExLink>
            </div>
            <div className="template-cards-container">
              {tools.length > 0
                ? tools.map((showTools: any) => (
                  <ExCard
                    className="tool-card"
                    title={showTools}
                    key={showTools}
                  >
                    <div
                      slot="header"
                      className="template-header"
                      data-testid="template-header"
                    >
                      <div className="tools-image-container">
                        <ExIcon
                          className="builder-tool-icon"
                          slot="icon"
                          icon={getIconByType(showTools)}
                        />
                      </div>
                    </div>
                    <div className="tools-content">
                      <div className="tool-name">{showTools}</div>
                      <div className="tool-description">
                        {getDescriptionByType(showTools)}
                      </div>
                    </div>
                    <div className="card-overlay">
                      <ExButton
                        flavor={ButtonFlavor.BRANDED}
                        type={ButtonType.PRIMARY}
                        size={ButtonSize.DEFAULT}
                        data-testid={`agent-designer-dialog-add-tool-${showTools}`}
                        onClick={() => handleClick(showTools)}
                      >
                        {t('add_tool')}
                      </ExButton>
                    </div>
                  </ExCard>
                ))
                : null}
            </div>
          </div>
        )}
        {isSourcesTabSelected && (
          <div className="designer-dialog">
            {/* eslint-disable-next-line */}
            <SourcesBuilderTabView hasAgentCreatePermission={hasAgentCreatePermission} userPrivileges={userPrivileges} />
          </div>
        )}
        {isModelsTabSelected && (
          <div className="designer-dialog">
            {/* eslint-disable-next-line */}
            <ModelBuilderTabView hasAgentCreatePermission={hasAgentCreatePermission} userPrivileges={userPrivileges} />
          </div>
        )}
      </div>
    </ExDialog>
  );
}
export default AgentBuilderDialog;
